import { createContext, useContext, useState } from "react";

const DICT = {
  en: {
    appName: "APHA",
    appTagline: "Agile Project Health Assistant",
    signIn: "Sign in",
    email: "Email",
    password: "Password",
    myWork: "MY WORK",
    delivery: "DELIVERY",
    reportsSection: "REPORTS",
    administration: "ADMINISTRATION",
    reportCenter: "Report Center",
    runReport: "Run report",
    exportCsv: "Export CSV",
    reportHistory: "Report history",
    period: "Period",
    periodFrom: "From",
    periodTo: "To",
    reportDate: "Report date",
    configuration: "Risk & Health Rules",
    configIntro: "Organization defaults for risk thresholds, health weights and escalation SLAs. Changes follow Draft → Validate → Activate; prior versions can be rolled back.",
    activeConfig: "Active",
    createDraft: "New draft",
    changeSummary: "Change summary",
    validate: "Validate",
    activate: "Activate",
    rollback: "Roll back",
    versionHistory: "Version history",
    blockerCenter: "Blocker Center",
    oldestBlocker: "Oldest blocker",
    openDependencies: "Open dependencies",
    unownedDecisions: "Unowned decisions",
    ageDays: "Age (days)",
    overdueFlag: "Overdue",
    unownedFlag: "No owner",
    lastReported: "Last reported",
    emailDigest: "Email & Digest",
    jiraTab: "Jira",
    accessGate: "Access Gate",
    gateTitle: "Access code required",
    gateSubtitle: "This environment is protected. Enter the shared access code to continue.",
    gateCodePlaceholder: "Access code",
    gateContinue: "Continue",
    gateInvalid: "Invalid access code",
    gateFootnote: "Verified server-side. Contact your administrator for the code.",
    gateAdminHint: "A site-wide access code required before anyone can reach the login page or use the API. Rotating the code immediately invalidates all existing gate passes.",
    gateEnabledLabel: "Require access code",
    gateRotateLabel: "New access code (min. 8 characters)",
    gateConfirmLabel: "Confirm new code",
    gateMismatch: "Codes do not match",
    gateRotated: "Access code updated — all existing passes revoked",
    gateActiveBadge: "ACTIVE",
    gateOffBadge: "OFF",
    gateLastRotated: "Last rotated",
    gateRotateWarning: "Rotating the code will sign every visitor out of the gate. Your own session gets a fresh pass automatically.",
    jiraConnection: "Jira Cloud connection",
    jiraSiteUrl: "Site URL",
    jiraAccountEmail: "Account email",
    jiraApiToken: "API token",
    jiraTokenSaved: "a token is saved, leave blank to keep it",
    jiraTokenHint: "Create a token at id.atlassian.com → Security → API tokens. It is encrypted at rest and never shown again.",
    jiraSaveConnection: "Save connection",
    jiraDisconnect: "Disconnect",
    jiraDisconnectConfirm: "Disconnect Jira? All project mappings and synced issues will be removed.",
    jiraDisconnected: "Jira disconnected — synced issues removed",
    jiraConfiguredBadge: "CONNECTED",
    jiraMockBadge: "NOT CONNECTED",
    jiraNotConnected: "NOT CONNECTED",
    jiraNotConfigured: "No Jira Cloud connection yet — projects have no Jira evidence until you connect your site and map them.",
    jiraConnectedAs: "Connected as",
    jiraProjectMapping: "Project mapping",
    jiraMappingHint: "Map each APHA project to a Jira Cloud project. Mapped projects sync live issues (assignee, status, due dates) used by suggestions, rules and health. Unmapped projects have no Jira evidence.",
    jiraCloudProject: "Jira Cloud project",
    jiraSelectProject: "Select Jira project",
    jiraUnmap: "Unmap",
    jiraSyncNow: "Sync now",
    jiraSource: "Source",
    jiraLastSync: "Last sync",
    jiraSyncedIssues: "Synced issues",
    jiraNoMapped: "No projects are mapped yet",
    jiraMockData: "unmapped",
    sprintBurndown: "Sprint Burndown",
    liveJira: "LIVE JIRA",
    demoData: "NOT CONNECTED",
    idealLine: "Ideal",
    remainingSeries: "Remaining",
    remainingLabel: "remaining",
    storyPoints: "story points",
    issuesLabel: "issues",
    digestBranding: "Digest branding",
    brandingHint: "Company branding applied to the header of every digest email.",
    companyName: "Company name",
    logoUrl: "Logo URL",
    accentColor: "Accent color",
    brandingPreview: "Email header preview",
    providerLabel: "Email provider",
    simulatedOutbox: "Simulated outbox (no real emails)",
    fromEmail: "From email",
    fromName: "From name",
    testSend: "Send test email",
    sendNow: "Send now",
    deliveryLog: "Email delivery log",
    digestSchedule: "Daily digest per project",
    digestIntro: "The Daily Scrum Digest is generated automatically every working morning and emailed to each project's recipients. Adjust recipients per project or send manually.",
    digestTime: "Send time (Asia/Jakarta)",
    digestEnabled: "Auto digest",
    recipientsLabel: "Recipients",
    saveSettings: "Save",
    myDaily: "My Daily Updates",
    myHistory: "My History",
    actionCenter: "Action Center",
    projects: "Projects",
    warnings: "Warnings & Risks",
    portfolio: "Portfolio",
    teamWorkload: "Team Workload",
    admin: "Administration",
    logout: "Sign out",
    notifications: "Notifications",
    markAllRead: "Mark all read",
    noNotifications: "No notifications",
    reportingDate: "Reporting date",
    deadline: "Due by",
    progressOf: (a, b) => `${a} of ${b} project updates submitted`,
    completeUpdate: "Complete update",
    continueDraft: "Continue draft",
    viewUpdate: "View update",
    expectedBecause: "Expected because",
    workloadCheck: "Daily Workload Check",
    workloadDone: "Workload check submitted",
    submitWorkload: "Submit workload check",
    availableCapacity: "Available capacity (%)",
    meetingLoad: "Meeting load",
    prodSupport: "Production support (%)",
    urgentWork: "Urgent non-project work",
    contextSwitch: "Context-switching concern",
    generalHelp: "Need general help?",
    notes: "Notes",
    clarificationsTitle: "Clarification requested",
    respond: "Respond",
    yourResponse: "Your response...",
    whatCompleted: "What was completed?",
    whatToday: "What will you work on today?",
    anyBlockers: "Are there any blockers?",
    blockerDesc: "Blocker description",
    blockedSince: "Blocked since",
    blockerImpact: "Impact",
    needHelp: "Need help?",
    helpNo: "No",
    helpSoon: "Yes — soon",
    helpNow: "Yes — now",
    helpCategory: "Help category",
    helpDetails: "Requested assistance",
    confidence: "Delivery confidence",
    onTrack: "On track",
    concerned: "Concerned",
    atRisk: "At risk",
    unknown: "Unknown",
    dependencies: "Dependencies",
    addDependency: "Add dependency",
    depDescription: "Description",
    depType: "Type",
    depOwner: "Owner",
    depNeededBy: "Needed by",
    milestone: "Target date / milestone impact",
    additionalContext: "Additional facts not in Jira",
    restrictedNote: "Sensitive/private note (restricted visibility)",
    relatedIssues: "Related Jira issues",
    suggestedIssues: "Suggested Jira issues",
    searchIssues: "Search issues...",
    saveDraft: "Save draft",
    draftSaved: "Draft saved",
    submitUpdate: "Submit update",
    submitted: "Submitted",
    late: "Late",
    editReason: "Reason for editing after cutoff",
    statuses: {
      HEALTHY: "Healthy", WATCH: "Watch", AT_RISK: "At Risk", CRITICAL: "Critical",
      INSUFFICIENT_DATA: "Insufficient Data", DUE: "Due", DRAFT: "Draft",
      SUBMITTED_ON_TIME: "Submitted", SUBMITTED_LATE: "Submitted late",
      MISSING: "Missing update", EXEMPT: "Exempt", NOT_REQUIRED: "Not required",
      NEW: "New", ACKNOWLEDGED: "Acknowledged", IN_PROGRESS: "In progress",
      RESOLVED: "Resolved", OVERRIDDEN: "Overridden", REOPENED: "Reopened",
      IMPROVING: "Improving", STABLE: "Stable", WORSENING: "Worsening",
      INSUFFICIENT_HISTORY: "Insufficient history",
    },
    severity: { CRITICAL: "Critical", HIGH: "High", MEDIUM: "Medium", LOW: "Low", INFO: "Info" },
    acknowledge: "Acknowledge",
    assign: "Assign",
    resolve: "Resolve",
    override: "Override / False positive",
    reopen: "Reopen",
    escalate: "Escalate",
    comment: "Comment",
    addIntervention: "Add intervention",
    intervention: "Intervention",
    interventionAction: "Action to take",
    targetDate: "Target date",
    complete: "Complete",
    remind: "Send reminder",
    markExempt: "Mark exception",
    acceptOwnership: "Accept ownership",
    aiSuggestions: "AI Suggested Actions",
    accept: "Accept",
    modify: "Modify",
    dismiss: "Dismiss",
    generateAiBrief: "Generate AI Morning Brief",
    generating: "Generating...",
    morningBrief: "Morning Brief",
    generatedBy: "Generated by",
    rulesOnly: "Rules-only",
    coverage: "Reporting coverage",
    healthScore: "Health score",
    trend: "Trend",
    evidenceConfidence: "Evidence confidence",
    completedOutcomes: "Completed yesterday",
    todayPriorities: "Today's priorities",
    activeBlockers: "Active blockers",
    decisionsNeeded: "Decisions needed",
    capacityConcerns: "Capacity concerns",
    missingReporters: "Missing reporters",
    recommendedActions: "Recommended actions",
    reporters: "Reporters",
    jiraMovement: "Jira Delivery Movement",
    doneRecently: "Completed",
    inProgress: "In progress",
    staleItems: "Stale items",
    interventionTracking: "Intervention tracking",
    healthTrend: "Health trend (7 periods)",
    mockJira: "Jira: not connected",
    evidence: "Evidence",
    age: "Age",
    owner: "Owner",
    source: "Source",
    slaBreached: "Awaiting acknowledgement — SLA passed",
    filters: "Filters",
    all: "All",
    severityLabel: "Severity",
    statusLabel: "Status",
    categoryLabel: "Category",
    projectLabel: "Project",
    topRisks: "Top risks requiring attention",
    overdueInterventions: "Intervention overdue",
    capacityReview: "Capacity review recommended",
    contextSwitchFlag: "Context switching",
    lowCapacity: "Low declared capacity",
    assistanceRequested: "Assistance requested",
    totalAllocation: "Total allocation",
    activeWip: "Active WIP",
    users: "Users",
    holidays: "Holidays",
    integrations: "Integration Health",
    auditLog: "Audit Log",
    runDailyCycle: "Run daily cycle",
    testConnection: "Test connection",
    addUser: "Add user",
    addHoliday: "Add holiday",
    active: "Active",
    inactive: "Inactive",
    loading: "Loading...",
    noData: "Nothing here yet",
    versions: "versions",
    workloadHistory: "Workload history",
    myUpdates: "My updates",
    helpNeeded: "Help needed",
    blockerFlag: "Blocker",
    summaryCounts: (c, h, m) => `${c} critical · ${h} high · ${m} medium`,
    changePassword: "Change password",
    currentPassword: "Current password",
    newPassword: "New password",
    confirmPassword: "Confirm new password",
    passwordMismatch: "Passwords do not match",
    passwordChanged: "Password updated",
    passwordMinHint: "Minimum 8 characters",
    resetPassword: "Reset password",
    passwordReset: "Password reset",
    initialPassword: "Initial password",
    name: "Name",
    role: "Role",
    save: "Save",
    cancel: "Cancel",
    remove: "Remove",
    addProject: "Add project",
    editProject: "Edit project",
    manageMembers: "Manage team",
    projectKey: "Key",
    projectName: "Project name",
    client: "Client",
    newClient: "New client…",
    clientName: "Client name",
    description: "Description",
    projectManager: "Project manager",
    scrumMaster: "Scrum master",
    reportWindow: "Reporting window (Asia/Jakarta)",
    opensAt: "Opens",
    dueAt: "Due",
    nextMilestone: "Next milestone",
    members: "Members",
    addMember: "Add member",
    projectRole: "Project role",
    allocation: "Allocation %",
    reportingRequired: "Daily report required",
    selectUser: "Select user",
    selectPm: "Select project manager",
    selectClient: "Select client",
    projectStatus: "Status",
    noMembers: "No team members yet — add people so their daily updates are expected.",
    noProjectsAdmin: "No projects yet. Create your first project to start collecting daily updates.",
    noProjectsHint: "No projects yet — an administrator can create one in Administration → Projects.",
    goToAdmin: "Open Administration",
    projectCreated: "Project created",
    projectUpdated: "Project updated",
    memberAdded: "Member added",
    memberRemoved: "Member removed",
    dangerZone: "Danger zone",
    purgeDemoTitle: "Purge demo data",
    purgeDemoHint: "Demo data from the initial seed is still present. Purging deletes all demo accounts (*@demo.local), projects, memberships, daily updates, warnings, health history, notifications and report runs. Your admin account, other users, settings and the audit log are kept.",
    purgeDemoConfirmLabel: "Type PURGE to confirm",
    purgeDemoButton: "Purge demo data",
    purgeDemoDone: "Demo data removed",
    noDemoData: "No demo data present — this environment is clean.",
    securityTitle: "Account security",
    sendInvite: "Email the user a set-password link",
    alsoSendInvite: "Email a set-password link",
    optionalWithLink: "optional — the user sets their own via the emailed link",
    tempPasswordNote: "Passwords are never emailed. If you set one here, the user must change it at first sign-in.",
    sendSetupLink: "Send set-password link",
    setupLinkSent: "Set-password link sent",
    setPassword: "Set your password",
    setPasswordAndSignIn: "Set password and sign in",
    passwordLinkInvalid: "This link is invalid or has expired. Ask your administrator to send a new one.",
    passwordLinkOnce: "This link works once and expires 24 hours after it was sent.",
    passwordChangeRequired: "Choose a new password",
    passwordChangeRequiredHint: "Your administrator set a temporary password. Pick your own before continuing.",
    inviteSent: "Invite email sent",
    inviteSimulated: "Invite logged to the simulated outbox — configure an email provider in Admin → Email to deliver it",
    inviteFailed: "Invite email failed",
    resendInvite: "Resend invite",
    invitedOn: "Invited",
    thumbsUp: "Thumbs up",
    addComment: "Add a short comment…",
    commentSent: "Comment sent",
    reactionsReceived: "Reactions from your PM",
    noReactions: "No reactions yet",
    showUpdate: "Show update",
    hideUpdate: "Hide update",
    notSubmittedYet: "Not submitted yet",
    work: "Work",
    board: "Board",
    backlog: "Backlog",
    workItems: "Work items",
    workflow: "Workflow",
    overview: "Overview",
    myWorkPage: "My Work",
    create: "Create",
    createWorkItem: "Create work item",
    type: "Type",
    title: "Title",
    priority: "Priority",
    assignee: "Assignee",
    reporter: "Reporter",
    unassigned: "Unassigned",
    labels: "Labels",
    labelsHint: "Comma separated",
    storyPointsLabel: "Story points",
    estimateHours: "Estimate (h)",
    remainingHours: "Remaining (h)",
    plannedStart: "Planned start",
    dueDate: "Due date",
    parent: "Parent",
    noParent: "No parent",
    sprint: "Sprint",
    noSprint: "Backlog (no sprint)",
    acceptanceCriteria: "Acceptance criteria",
    childItems: "Child items",
    addChild: "Add child",
    links: "Links",
    linkItem: "Link item",
    targetKey: "Item key (e.g. EMS-12)",
    activity: "Activity",
    writeComment: "Write a comment… type @ to mention",
    post: "Post",
    watch: "Watch",
    unwatch: "Unwatch",
    watchers: "Watchers",
    moveTo: "Move to…",
    statusHistory: "Status history",
    blocked: "Blocked",
    BLOCKS: "Blocks",
    BLOCKED_BY: "Blocked by",
    RELATES: "Relates to",
    DUPLICATES: "Duplicates",
    DUPLICATED_BY: "Duplicated by",
    swimlanes: "Swimlanes",
    none: "None",
    byAssignee: "By assignee",
    byEpic: "By epic",
    noEpic: "No epic",
    searchWork: "Search work items (key or title)…",
    createSprint: "Create sprint",
    startSprint: "Start sprint",
    completeSprint: "Complete sprint",
    editSprint: "Edit sprint",
    sprintName: "Sprint name",
    sprintGoal: "Sprint goal",
    startDate: "Start date",
    endDate: "End date",
    plannedSprint: "Planned",
    activeSprint: "Active",
    completedSprint: "Completed",
    moveUnfinishedTo: "Move unfinished items to",
    committed: "Committed",
    scopeChanges: "Scope changes",
    sprintReport: "Sprint report",
    burndown: "Burndown",
    noActiveSprint: "No active sprint — start one from the backlog",
    dragHint: "Drag items between the backlog and sprints to plan. Drag within a list to re-rank.",
    quickCreate: "What needs to be done? Press Enter to create",
    statusesLabel: "Statuses",
    transitions: "Transitions",
    category: "Category",
    initialStatus: "Initial",
    sideState: "Side state",
    addStatus: "Add status",
    saveWorkflow: "Save workflow",
    validators: "Validators",
    allowedRoles: "Allowed roles (none selected = everyone with write access)",
    transitionMatrixHint: "Rows are the current status, columns the target. Tick a cell to allow the transition; click a ticked cell's gear to set roles and validators.",
    workflowSaved: "Workflow saved",
    itemCreated: "Work item created",
    saved: "Saved",
    moved: "Moved",
    assignedToMe: "Assigned to me",
    watching: "Watching",
    noWorkItems: "No work items yet — create the first one.",
    deleteItem: "Delete item",
    confirmDelete: "Delete this work item? This cannot be undone.",
    kanbanNoSprints: "Kanban projects use continuous flow — sprints are disabled.",
    methodology: "Methodology",
    nativeBoard: "BOARD",
    evidenceBoard: "Board evidence",
    openBoard: "Open board",
    items: "items",
    points: "pts",
    hours: "h",
    done: "Done",
    clearFilters: "Clear",
    showCanceled: "Show canceled",
    quickFilters: "Quick filters",
    saveView: "Save view",
    viewNamePh: "e.g. My team's blockers",
    shareWithProject: "Share with project team",
    share: "Share with team",
    unshare: "Stop sharing",
    shared: "Shared with team",
    viewShared: "View shared with the team",
    viewUnshared: "View is now private",
    sharedBy: "Shared by",
    deleteView: "Delete view",
    viewSaved: "View saved",
    viewDeleted: "View deleted",
    presetMine: "My items",
    presetBlocked: "Blocked",
    blockedOnly: "Blocked only",
    me: "Me",
    noFiltersToSave: "Set a filter first, then save it as a view",
    key: "Key",
    status: "Status",
    updated: "Updated",
    created: "Created",
    inSprint: "In sprint",
    childrenNotDone: "children not done",
    version: "Version",
    TODO: "To Do",
    IN_PROGRESS: "In Progress",
    DONE: "Done",
    NO_OPEN_BLOCKERS: "No open blockers",
    CHILDREN_DONE: "All children done",
    REQUIRE_ASSIGNEE: "Assignee required",
    EPIC: "Epic", STORY: "Story", TASK: "Task", SUBTASK: "Subtask", BUG: "Bug", CHANGE: "Change", ACTION: "Action",
    HIGHEST: "Highest", HIGH: "High", MEDIUM: "Medium", LOW: "Low", LOWEST: "Lowest",
    workQuery: "Work Query",
    workQueryIntro: "Search work items across all your projects with a JQL-like query. Press Enter or Run to execute; click a row to open the item.",
    queryMode: "Query",
    queryModeHint: "Switch to a JQL-like query, e.g. assignee = me AND status != Done",
    queryPlaceholder: "assignee = me AND status != Done ORDER BY priority ASC",
    runQuery: "Run",
    queryHelp: "Query help",
    queryExamples: "Examples",
    queryFields: "Fields",
    operators: "Operators",
    recentQueries: "Recent",
    timeTracking: "Time tracking",
    logWork: "Log work",
    hoursSpent: "Hours spent",
    workDate: "Work date",
    newRemaining: "New remaining (h)",
    autoRemaining: "auto",
    note: "Note",
    workLogged: "Work logged",
    worklog: "Work log",
    worklogRemoved: "Work log removed",
    noWorklogs: "No work logged yet",
    loggedShort: "logged",
    remainingShort: "remaining",
    estimateShort: "estimate",
    total: "Total",
    price: "Price",
    priceOptional: "optional",
    label: "Label",
    goal: "Goal",
    workItemsLoad: "Work items",
    openItems: "Open",
    inProgressShort: "In progress",
    blockedShort: "Blocked",
    overdueShort: "Overdue",
    pointsOpen: "Points (open)",
    loggedInPeriod: "Logged",
    doneInPeriod: "Done",
    periodDays: "Last {n} days",
    noNativeWork: "No work items assigned",
    workloadIntro: "Allocation, declared capacity and native work-item load per person across your projects. This view supports capacity conversations and assistance — it is not a ranking.",
    openReport: "Open report (CSV)",
    epicRollup: "Epics · budget & hours",
    epic: "Epic",
    epics: "Epics",
    progress: "Progress",
    delivered: "Delivered",
    budgetBurn: "Budget burn",
    pricedItems: "priced items",
  },
  id: {
    appName: "APHA",
    appTagline: "Asisten Kesehatan Proyek Agile",
    signIn: "Masuk",
    email: "Email",
    password: "Kata sandi",
    myWork: "PEKERJAAN SAYA",
    delivery: "DELIVERY",
    reportsSection: "LAPORAN",
    administration: "ADMINISTRASI",
    reportCenter: "Pusat Laporan",
    runReport: "Jalankan laporan",
    exportCsv: "Ekspor CSV",
    reportHistory: "Riwayat laporan",
    period: "Periode",
    periodFrom: "Dari",
    periodTo: "Sampai",
    reportDate: "Tanggal laporan",
    configuration: "Aturan Risiko & Kesehatan",
    configIntro: "Nilai default organisasi untuk ambang risiko, bobot kesehatan, dan SLA eskalasi. Perubahan mengikuti Draf → Validasi → Aktifkan; versi sebelumnya dapat dikembalikan.",
    activeConfig: "Aktif",
    createDraft: "Draf baru",
    changeSummary: "Ringkasan perubahan",
    validate: "Validasi",
    activate: "Aktifkan",
    rollback: "Kembalikan",
    versionHistory: "Riwayat versi",
    blockerCenter: "Pusat Hambatan",
    oldestBlocker: "Hambatan tertua",
    openDependencies: "Ketergantungan terbuka",
    unownedDecisions: "Keputusan tanpa pemilik",
    ageDays: "Usia (hari)",
    overdueFlag: "Terlambat",
    unownedFlag: "Tanpa pemilik",
    lastReported: "Terakhir dilaporkan",
    emailDigest: "Email & Ringkasan",
    jiraTab: "Jira",
    accessGate: "Gerbang Akses",
    gateTitle: "Kode akses diperlukan",
    gateSubtitle: "Lingkungan ini dilindungi. Masukkan kode akses bersama untuk melanjutkan.",
    gateCodePlaceholder: "Kode akses",
    gateContinue: "Lanjutkan",
    gateInvalid: "Kode akses tidak valid",
    gateFootnote: "Diverifikasi di sisi server. Hubungi administrator untuk mendapatkan kode.",
    gateAdminHint: "Kode akses situs yang wajib dimasukkan sebelum siapa pun dapat membuka halaman login atau memakai API. Merotasi kode langsung membatalkan semua gate pass yang ada.",
    gateEnabledLabel: "Wajibkan kode akses",
    gateRotateLabel: "Kode akses baru (min. 8 karakter)",
    gateConfirmLabel: "Konfirmasi kode baru",
    gateMismatch: "Kode tidak cocok",
    gateRotated: "Kode akses diperbarui — semua pass lama dicabut",
    gateActiveBadge: "AKTIF",
    gateOffBadge: "NONAKTIF",
    gateLastRotated: "Terakhir dirotasi",
    gateRotateWarning: "Merotasi kode akan mengeluarkan semua pengunjung dari gerbang. Sesi Anda otomatis mendapat pass baru.",
    jiraConnection: "Koneksi Jira Cloud",
    jiraSiteUrl: "URL Situs",
    jiraAccountEmail: "Email akun",
    jiraApiToken: "Token API",
    jiraTokenSaved: "token sudah tersimpan, kosongkan untuk mempertahankannya",
    jiraTokenHint: "Buat token di id.atlassian.com → Security → API tokens. Token dienkripsi saat disimpan dan tidak ditampilkan lagi.",
    jiraSaveConnection: "Simpan koneksi",
    jiraDisconnect: "Putuskan",
    jiraDisconnectConfirm: "Putuskan Jira? Semua pemetaan proyek dan issue yang tersinkron akan dihapus.",
    jiraDisconnected: "Jira terputus — issue tersinkron dihapus",
    jiraConfiguredBadge: "TERHUBUNG",
    jiraMockBadge: "BELUM TERHUBUNG",
    jiraNotConnected: "BELUM TERHUBUNG",
    jiraNotConfigured: "Belum ada koneksi Jira Cloud — proyek belum punya bukti Jira sampai Anda menghubungkan situs dan memetakannya.",
    jiraConnectedAs: "Terhubung sebagai",
    jiraProjectMapping: "Pemetaan proyek",
    jiraMappingHint: "Petakan setiap proyek APHA ke proyek Jira Cloud. Proyek yang dipetakan menyinkronkan issue nyata (penanggung jawab, status, tenggat) yang dipakai saran, aturan, dan kesehatan. Proyek yang tidak dipetakan tidak punya bukti Jira.",
    jiraCloudProject: "Proyek Jira Cloud",
    jiraSelectProject: "Pilih proyek Jira",
    jiraUnmap: "Lepas pemetaan",
    jiraSyncNow: "Sinkronkan sekarang",
    jiraSource: "Sumber",
    jiraLastSync: "Sinkron terakhir",
    jiraSyncedIssues: "Issue tersinkron",
    jiraNoMapped: "Belum ada proyek yang dipetakan",
    jiraMockData: "belum dipetakan",
    sprintBurndown: "Burndown Sprint",
    liveJira: "JIRA LANGSUNG",
    demoData: "BELUM TERHUBUNG",
    idealLine: "Ideal",
    remainingSeries: "Tersisa",
    remainingLabel: "tersisa",
    storyPoints: "story point",
    issuesLabel: "issue",
    digestBranding: "Branding ringkasan",
    brandingHint: "Branding perusahaan diterapkan pada header setiap email ringkasan.",
    companyName: "Nama perusahaan",
    logoUrl: "URL logo",
    accentColor: "Warna aksen",
    brandingPreview: "Pratinjau header email",
    providerLabel: "Penyedia email",
    simulatedOutbox: "Outbox simulasi (tanpa email nyata)",
    fromEmail: "Email pengirim",
    fromName: "Nama pengirim",
    testSend: "Kirim email uji",
    sendNow: "Kirim sekarang",
    deliveryLog: "Log pengiriman email",
    digestSchedule: "Ringkasan harian per proyek",
    digestIntro: "Ringkasan Scrum Harian dibuat otomatis setiap pagi hari kerja dan dikirim ke email penerima tiap proyek. Atur penerima per proyek atau kirim manual.",
    digestTime: "Waktu kirim (Asia/Jakarta)",
    digestEnabled: "Ringkasan otomatis",
    recipientsLabel: "Penerima",
    saveSettings: "Simpan",
    myDaily: "Laporan Harian Saya",
    myHistory: "Riwayat Saya",
    actionCenter: "Pusat Aksi",
    projects: "Proyek",
    warnings: "Peringatan & Risiko",
    portfolio: "Portofolio",
    teamWorkload: "Beban Kerja Tim",
    admin: "Administrasi",
    logout: "Keluar",
    notifications: "Notifikasi",
    markAllRead: "Tandai semua dibaca",
    noNotifications: "Tidak ada notifikasi",
    reportingDate: "Tanggal pelaporan",
    deadline: "Batas waktu",
    progressOf: (a, b) => `${a} dari ${b} laporan proyek terkirim`,
    completeUpdate: "Isi laporan",
    continueDraft: "Lanjutkan draf",
    viewUpdate: "Lihat laporan",
    expectedBecause: "Diharapkan karena",
    workloadCheck: "Cek Beban Kerja Harian",
    workloadDone: "Cek beban kerja terkirim",
    submitWorkload: "Kirim cek beban kerja",
    availableCapacity: "Kapasitas tersedia (%)",
    meetingLoad: "Beban rapat",
    prodSupport: "Dukungan produksi (%)",
    urgentWork: "Pekerjaan mendesak non-proyek",
    contextSwitch: "Kekhawatiran perpindahan konteks",
    generalHelp: "Butuh bantuan umum?",
    notes: "Catatan",
    clarificationsTitle: "Klarifikasi diminta",
    respond: "Jawab",
    yourResponse: "Jawaban Anda...",
    whatCompleted: "Apa yang telah diselesaikan?",
    whatToday: "Apa yang akan dikerjakan hari ini?",
    anyBlockers: "Apakah ada hambatan?",
    blockerDesc: "Deskripsi hambatan",
    blockedSince: "Terhambat sejak",
    blockerImpact: "Dampak",
    needHelp: "Membutuhkan bantuan?",
    helpNo: "Tidak",
    helpSoon: "Ya — segera",
    helpNow: "Ya — sekarang",
    helpCategory: "Kategori bantuan",
    helpDetails: "Bantuan yang diminta",
    confidence: "Keyakinan pengiriman",
    onTrack: "Sesuai rencana",
    concerned: "Perlu perhatian",
    atRisk: "Berisiko",
    unknown: "Tidak diketahui",
    dependencies: "Ketergantungan",
    addDependency: "Tambah ketergantungan",
    depDescription: "Deskripsi",
    depType: "Tipe",
    depOwner: "Pemilik",
    depNeededBy: "Dibutuhkan sebelum",
    milestone: "Tanggal target / dampak milestone",
    additionalContext: "Fakta tambahan di luar Jira",
    restrictedNote: "Catatan sensitif/pribadi (akses terbatas)",
    relatedIssues: "Isu Jira terkait",
    suggestedIssues: "Isu Jira yang disarankan",
    searchIssues: "Cari isu...",
    saveDraft: "Simpan draf",
    draftSaved: "Draf tersimpan",
    submitUpdate: "Kirim laporan",
    submitted: "Terkirim",
    late: "Terlambat",
    editReason: "Alasan pengeditan setelah batas waktu",
    statuses: {
      HEALTHY: "Sehat", WATCH: "Perlu Dipantau", AT_RISK: "Berisiko", CRITICAL: "Kritis",
      INSUFFICIENT_DATA: "Data Tidak Cukup", DUE: "Jatuh Tempo", DRAFT: "Draf",
      SUBMITTED_ON_TIME: "Terkirim", SUBMITTED_LATE: "Terkirim terlambat",
      MISSING: "Laporan belum disampaikan", EXEMPT: "Dikecualikan", NOT_REQUIRED: "Tidak diperlukan",
      NEW: "Baru", ACKNOWLEDGED: "Ditinjau", IN_PROGRESS: "Sedang berjalan",
      RESOLVED: "Selesai", OVERRIDDEN: "Ditimpa", REOPENED: "Dibuka kembali",
      IMPROVING: "Membaik", STABLE: "Stabil", WORSENING: "Memburuk",
      INSUFFICIENT_HISTORY: "Riwayat tidak cukup",
    },
    severity: { CRITICAL: "Kritis", HIGH: "Tinggi", MEDIUM: "Sedang", LOW: "Rendah", INFO: "Info" },
    acknowledge: "Tinjau dan akui",
    assign: "Tugaskan",
    resolve: "Selesaikan",
    override: "Timpa / Positif palsu",
    reopen: "Buka kembali",
    escalate: "Eskalasi",
    comment: "Komentar",
    addIntervention: "Tambah intervensi",
    intervention: "Intervensi",
    interventionAction: "Aksi yang diambil",
    targetDate: "Tanggal target",
    complete: "Selesaikan",
    remind: "Kirim pengingat",
    markExempt: "Tandai pengecualian",
    acceptOwnership: "Terima kepemilikan",
    aiSuggestions: "Saran Aksi AI",
    accept: "Terima",
    modify: "Ubah",
    dismiss: "Abaikan",
    generateAiBrief: "Buat Ringkasan Pagi AI",
    generating: "Membuat...",
    morningBrief: "Ringkasan Pagi",
    generatedBy: "Dibuat oleh",
    rulesOnly: "Hanya aturan",
    coverage: "Cakupan pelaporan",
    healthScore: "Skor kesehatan",
    trend: "Tren",
    evidenceConfidence: "Keyakinan bukti",
    completedOutcomes: "Selesai kemarin",
    todayPriorities: "Prioritas hari ini",
    activeBlockers: "Hambatan aktif",
    decisionsNeeded: "Keputusan dibutuhkan",
    capacityConcerns: "Kekhawatiran kapasitas",
    missingReporters: "Pelapor belum lapor",
    recommendedActions: "Aksi yang direkomendasikan",
    reporters: "Pelapor",
    jiraMovement: "Pergerakan Jira",
    doneRecently: "Selesai",
    inProgress: "Sedang berjalan",
    staleItems: "Item mandek",
    interventionTracking: "Pelacakan intervensi",
    healthTrend: "Tren kesehatan (7 periode)",
    mockJira: "Jira: belum terhubung",
    evidence: "Bukti",
    age: "Usia",
    owner: "Pemilik",
    source: "Sumber",
    slaBreached: "Menunggu peninjauan — SLA terlewati",
    filters: "Filter",
    all: "Semua",
    severityLabel: "Tingkat",
    statusLabel: "Status",
    categoryLabel: "Kategori",
    projectLabel: "Proyek",
    topRisks: "Risiko utama yang perlu perhatian",
    overdueInterventions: "Intervensi terlambat",
    capacityReview: "Tinjauan kapasitas direkomendasikan",
    contextSwitchFlag: "Perpindahan konteks",
    lowCapacity: "Kapasitas rendah dilaporkan",
    assistanceRequested: "Bantuan diminta",
    totalAllocation: "Total alokasi",
    activeWip: "WIP aktif",
    users: "Pengguna",
    holidays: "Hari libur",
    integrations: "Kesehatan Integrasi",
    auditLog: "Log Audit",
    runDailyCycle: "Jalankan siklus harian",
    testConnection: "Uji koneksi",
    addUser: "Tambah pengguna",
    addHoliday: "Tambah hari libur",
    active: "Aktif",
    inactive: "Nonaktif",
    loading: "Memuat...",
    noData: "Belum ada data",
    versions: "versi",
    workloadHistory: "Riwayat beban kerja",
    myUpdates: "Laporan saya",
    helpNeeded: "Butuh bantuan",
    blockerFlag: "Hambatan",
    summaryCounts: (c, h, m) => `${c} kritis · ${h} tinggi · ${m} sedang`,
    changePassword: "Ganti kata sandi",
    currentPassword: "Kata sandi saat ini",
    newPassword: "Kata sandi baru",
    confirmPassword: "Konfirmasi kata sandi baru",
    passwordMismatch: "Kata sandi tidak cocok",
    passwordChanged: "Kata sandi diperbarui",
    passwordMinHint: "Minimal 8 karakter",
    resetPassword: "Atur ulang kata sandi",
    passwordReset: "Kata sandi diatur ulang",
    initialPassword: "Kata sandi awal",
    name: "Nama",
    role: "Peran",
    save: "Simpan",
    cancel: "Batal",
    remove: "Hapus",
    addProject: "Tambah proyek",
    editProject: "Ubah proyek",
    manageMembers: "Kelola tim",
    projectKey: "Kode",
    projectName: "Nama proyek",
    client: "Klien",
    newClient: "Klien baru…",
    clientName: "Nama klien",
    description: "Deskripsi",
    projectManager: "Manajer proyek",
    scrumMaster: "Scrum master",
    reportWindow: "Jendela pelaporan (Asia/Jakarta)",
    opensAt: "Dibuka",
    dueAt: "Batas",
    nextMilestone: "Milestone berikutnya",
    members: "Anggota",
    addMember: "Tambah anggota",
    projectRole: "Peran di proyek",
    allocation: "Alokasi %",
    reportingRequired: "Wajib laporan harian",
    selectUser: "Pilih pengguna",
    selectPm: "Pilih manajer proyek",
    selectClient: "Pilih klien",
    projectStatus: "Status",
    noMembers: "Belum ada anggota tim — tambahkan orang agar laporan hariannya diharapkan.",
    noProjectsAdmin: "Belum ada proyek. Buat proyek pertama untuk mulai mengumpulkan laporan harian.",
    noProjectsHint: "Belum ada proyek — administrator dapat membuatnya di Administrasi → Proyek.",
    goToAdmin: "Buka Administrasi",
    projectCreated: "Proyek dibuat",
    projectUpdated: "Proyek diperbarui",
    memberAdded: "Anggota ditambahkan",
    memberRemoved: "Anggota dihapus",
    dangerZone: "Zona berbahaya",
    purgeDemoTitle: "Hapus data demo",
    purgeDemoHint: "Data demo dari seed awal masih ada. Penghapusan akan menghilangkan semua akun demo (*@demo.local), proyek, keanggotaan, laporan harian, peringatan, riwayat kesehatan, notifikasi, dan riwayat laporan. Akun admin Anda, pengguna lain, pengaturan, dan log audit tetap disimpan.",
    purgeDemoConfirmLabel: "Ketik PURGE untuk konfirmasi",
    purgeDemoButton: "Hapus data demo",
    purgeDemoDone: "Data demo dihapus",
    noDemoData: "Tidak ada data demo — lingkungan ini bersih.",
    securityTitle: "Keamanan akun",
    sendInvite: "Kirim tautan atur kata sandi ke email pengguna",
    alsoSendInvite: "Kirim tautan atur kata sandi via email",
    optionalWithLink: "opsional — pengguna mengatur sendiri lewat tautan email",
    tempPasswordNote: "Kata sandi tidak pernah dikirim via email. Jika diisi di sini, pengguna wajib menggantinya saat masuk pertama kali.",
    sendSetupLink: "Kirim tautan atur kata sandi",
    setupLinkSent: "Tautan atur kata sandi terkirim",
    setPassword: "Atur kata sandi Anda",
    setPasswordAndSignIn: "Atur kata sandi dan masuk",
    passwordLinkInvalid: "Tautan ini tidak valid atau sudah kedaluwarsa. Minta administrator mengirim tautan baru.",
    passwordLinkOnce: "Tautan ini hanya berlaku sekali dan kedaluwarsa 24 jam setelah dikirim.",
    passwordChangeRequired: "Pilih kata sandi baru",
    passwordChangeRequiredHint: "Administrator mengatur kata sandi sementara. Pilih kata sandi Anda sendiri sebelum melanjutkan.",
    inviteSent: "Email undangan terkirim",
    inviteSimulated: "Undangan dicatat di outbox simulasi — atur penyedia email di Admin → Email untuk mengirimnya",
    inviteFailed: "Email undangan gagal",
    resendInvite: "Kirim ulang undangan",
    invitedOn: "Diundang",
    thumbsUp: "Jempol",
    addComment: "Tulis komentar singkat…",
    commentSent: "Komentar terkirim",
    reactionsReceived: "Reaksi dari PM Anda",
    noReactions: "Belum ada reaksi",
    showUpdate: "Lihat laporan",
    hideUpdate: "Sembunyikan laporan",
    notSubmittedYet: "Belum dikirim",
    work: "Pekerjaan",
    board: "Papan",
    backlog: "Backlog",
    workItems: "Item pekerjaan",
    workflow: "Alur kerja",
    overview: "Ringkasan",
    myWorkPage: "Pekerjaan Saya",
    create: "Buat",
    createWorkItem: "Buat item pekerjaan",
    type: "Tipe",
    title: "Judul",
    priority: "Prioritas",
    assignee: "Penanggung jawab",
    reporter: "Pelapor",
    unassigned: "Belum ditugaskan",
    labels: "Label",
    labelsHint: "Pisahkan dengan koma",
    storyPointsLabel: "Story point",
    estimateHours: "Estimasi (jam)",
    remainingHours: "Sisa (jam)",
    plannedStart: "Mulai rencana",
    dueDate: "Tenggat",
    parent: "Induk",
    noParent: "Tanpa induk",
    sprint: "Sprint",
    noSprint: "Backlog (tanpa sprint)",
    acceptanceCriteria: "Kriteria penerimaan",
    childItems: "Item anak",
    addChild: "Tambah anak",
    links: "Tautan",
    linkItem: "Tautkan item",
    targetKey: "Kode item (mis. EMS-12)",
    activity: "Aktivitas",
    writeComment: "Tulis komentar… ketik @ untuk menyebut",
    post: "Kirim",
    watch: "Ikuti",
    unwatch: "Berhenti ikuti",
    watchers: "Pengikut",
    moveTo: "Pindah ke…",
    statusHistory: "Riwayat status",
    blocked: "Terhambat",
    BLOCKS: "Menghambat",
    BLOCKED_BY: "Dihambat oleh",
    RELATES: "Terkait dengan",
    DUPLICATES: "Duplikat dari",
    DUPLICATED_BY: "Diduplikasi oleh",
    swimlanes: "Jalur",
    none: "Tidak ada",
    byAssignee: "Per penanggung jawab",
    byEpic: "Per epic",
    noEpic: "Tanpa epic",
    searchWork: "Cari item pekerjaan (kode atau judul)…",
    createSprint: "Buat sprint",
    startSprint: "Mulai sprint",
    completeSprint: "Selesaikan sprint",
    editSprint: "Ubah sprint",
    sprintName: "Nama sprint",
    sprintGoal: "Tujuan sprint",
    startDate: "Tanggal mulai",
    endDate: "Tanggal selesai",
    plannedSprint: "Direncanakan",
    activeSprint: "Aktif",
    completedSprint: "Selesai",
    moveUnfinishedTo: "Pindahkan item belum selesai ke",
    committed: "Komitmen",
    scopeChanges: "Perubahan cakupan",
    sprintReport: "Laporan sprint",
    burndown: "Burndown",
    noActiveSprint: "Belum ada sprint aktif — mulai dari backlog",
    dragHint: "Seret item antara backlog dan sprint untuk merencanakan. Seret dalam daftar untuk mengurutkan.",
    quickCreate: "Apa yang perlu dikerjakan? Tekan Enter untuk membuat",
    statusesLabel: "Status",
    transitions: "Transisi",
    category: "Kategori",
    initialStatus: "Awal",
    sideState: "Status samping",
    addStatus: "Tambah status",
    saveWorkflow: "Simpan alur kerja",
    validators: "Validator",
    allowedRoles: "Peran yang diizinkan (kosong = semua yang punya akses tulis)",
    transitionMatrixHint: "Baris adalah status saat ini, kolom adalah tujuan. Centang sel untuk mengizinkan transisi; klik ikon gir pada sel tercentang untuk mengatur peran dan validator.",
    workflowSaved: "Alur kerja disimpan",
    itemCreated: "Item pekerjaan dibuat",
    saved: "Tersimpan",
    moved: "Dipindahkan",
    assignedToMe: "Ditugaskan ke saya",
    watching: "Diikuti",
    noWorkItems: "Belum ada item pekerjaan — buat yang pertama.",
    deleteItem: "Hapus item",
    confirmDelete: "Hapus item pekerjaan ini? Tidak dapat dibatalkan.",
    kanbanNoSprints: "Proyek Kanban memakai alur berkelanjutan — sprint dinonaktifkan.",
    methodology: "Metodologi",
    nativeBoard: "PAPAN",
    evidenceBoard: "Bukti papan",
    openBoard: "Buka papan",
    items: "item",
    points: "poin",
    hours: "jam",
    done: "Selesai",
    clearFilters: "Bersihkan",
    showCanceled: "Tampilkan dibatalkan",
    quickFilters: "Filter cepat",
    saveView: "Simpan tampilan",
    viewNamePh: "mis. Penghambat tim saya",
    shareWithProject: "Bagikan ke tim proyek",
    share: "Bagikan ke tim",
    unshare: "Berhenti membagikan",
    shared: "Dibagikan ke tim",
    viewShared: "Tampilan dibagikan ke tim",
    viewUnshared: "Tampilan kini privat",
    sharedBy: "Dibagikan oleh",
    deleteView: "Hapus tampilan",
    viewSaved: "Tampilan disimpan",
    viewDeleted: "Tampilan dihapus",
    presetMine: "Item saya",
    presetBlocked: "Terhambat",
    blockedOnly: "Hanya terhambat",
    me: "Saya",
    noFiltersToSave: "Atur filter dulu, lalu simpan sebagai tampilan",
    key: "Kode",
    status: "Status",
    updated: "Diperbarui",
    created: "Dibuat",
    inSprint: "Dalam sprint",
    childrenNotDone: "anak belum selesai",
    version: "Versi",
    TODO: "Belum Dikerjakan",
    IN_PROGRESS: "Dikerjakan",
    DONE: "Selesai",
    NO_OPEN_BLOCKERS: "Tidak ada penghambat terbuka",
    CHILDREN_DONE: "Semua anak selesai",
    REQUIRE_ASSIGNEE: "Wajib ada penanggung jawab",
    EPIC: "Epic", STORY: "Story", TASK: "Tugas", SUBTASK: "Subtugas", BUG: "Bug", CHANGE: "Perubahan", ACTION: "Aksi",
    HIGHEST: "Tertinggi", HIGH: "Tinggi", MEDIUM: "Sedang", LOW: "Rendah", LOWEST: "Terendah",
    workQuery: "Kueri Pekerjaan",
    workQueryIntro: "Cari item pekerjaan di semua proyek Anda dengan kueri mirip JQL. Tekan Enter atau Jalankan; klik baris untuk membuka item.",
    queryMode: "Kueri",
    queryModeHint: "Beralih ke kueri mirip JQL, mis. assignee = me AND status != Done",
    queryPlaceholder: "assignee = me AND status != Done ORDER BY priority ASC",
    runQuery: "Jalankan",
    queryHelp: "Bantuan kueri",
    queryExamples: "Contoh",
    queryFields: "Field",
    operators: "Operator",
    recentQueries: "Terakhir",
    timeTracking: "Pelacakan waktu",
    logWork: "Catat jam kerja",
    hoursSpent: "Jam terpakai",
    workDate: "Tanggal kerja",
    newRemaining: "Sisa baru (j)",
    autoRemaining: "otomatis",
    note: "Catatan",
    workLogged: "Jam kerja dicatat",
    worklog: "Log kerja",
    worklogRemoved: "Log kerja dihapus",
    noWorklogs: "Belum ada jam kerja dicatat",
    loggedShort: "tercatat",
    remainingShort: "sisa",
    estimateShort: "estimasi",
    total: "Total",
    price: "Harga",
    priceOptional: "opsional",
    label: "Label",
    goal: "Tujuan",
    workItemsLoad: "Item pekerjaan",
    openItems: "Terbuka",
    inProgressShort: "Dikerjakan",
    blockedShort: "Terblokir",
    overdueShort: "Terlambat",
    pointsOpen: "Poin (terbuka)",
    loggedInPeriod: "Tercatat",
    doneInPeriod: "Selesai",
    periodDays: "{n} hari terakhir",
    noNativeWork: "Tidak ada item pekerjaan",
    workloadIntro: "Alokasi, kapasitas yang dinyatakan, dan beban item pekerjaan per orang di seluruh proyek Anda. Tampilan ini mendukung percakapan kapasitas dan bantuan — bukan pemeringkatan.",
    openReport: "Buka laporan (CSV)",
    epicRollup: "Epic · anggaran & jam",
    epic: "Epic",
    epics: "Epic",
    progress: "Progres",
    delivered: "Terselesaikan",
    budgetBurn: "Realisasi anggaran",
    pricedItems: "item berharga",
  },
};

const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(localStorage.getItem("apha_lang") || "en");
  const setLang = (l) => {
    localStorage.setItem("apha_lang", l);
    setLangState(l);
  };
  const t = (key) => {
    const parts = key.split(".");
    let cur = DICT[lang];
    for (const p of parts) cur = cur?.[p];
    if (cur === undefined) {
      cur = DICT.en;
      for (const p of parts) cur = cur?.[p];
    }
    return cur ?? key;
  };
  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export function useLang() {
  return useContext(LangContext);
}
